import { Curriculum } from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import {
  Button,
  IconActionsMoreHoriz,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@twinkltech/web-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import { DeleteCurriculumModalContainer } from "@/components/DeleteCurriculumModal/DeleteCurriculumModal.container";
import { DuplicateCurriculumModalContainer } from "@/components/DuplicateCurriculumModal/DuplicateCurriculumModal.container";
import { EditCurriculumModalContainer } from "@/components/EditCurriculumModal/EditCurriculumModal.container";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { paths } from "@/paths";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { CURRICULUM_LIST_NS } from "../../CurriculumList.i18n";
import { ActionLink } from "../ActionLink/ActionLink";

export const ActionMenu = ({
  curriculum,
  shouldRedirectOnAction,
  ...props
}: {
  shouldRedirectOnAction?: boolean;
  curriculum: Curriculum;
} & React.HTMLProps<HTMLDivElement>) => {
  const { t } = useTranslation(CURRICULUM_LIST_NS);
  const { incrementEngagementCount } = useFeedback();

  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { click } = useTwinklAnalytics();

  return (
    <>
      <Popover {...props}>
        <PopoverTrigger asChild>
          <Button
            aria-label="Open action menu"
            className="data-[state=open]:bg-neutral-100"
            size="icon"
            variant="ghost"
          >
            <IconActionsMoreHoriz />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="p-2 z-1">
          <nav className="flex flex-col gap-2">
            <ActionLink
              variant="ghost"
              onClick={() => {
                setEditOpen(true);
                click("click_more_options ", {
                  clickCopy: "Edit Curriculum",
                });
              }}
            >
              Edit information
            </ActionLink>

            <ActionLink
              variant="ghost"
              onClick={() => {
                setDuplicateOpen(true);
                click("click_more_options ", {
                  clickCopy: "Duplicate Curriculum",
                });
              }}
            >
              {t("actions.duplicate", { ns: COMMON_NS })}
            </ActionLink>

            {!shouldRedirectOnAction && (
              <ActionLink
                as={Link}
                to={generatePath(paths.CURRICULUM_SUBJECT_LIST_PATH, {
                  curriculum_id: curriculum.id || "",
                })}
                variant="ghost"
                onClick={() => {
                  incrementEngagementCount();
                  click("click_more_options ", {
                    clickCopy: "View Curriculum",
                  });
                }}
              >
                {t("actions.view", { ns: COMMON_NS })}
              </ActionLink>
            )}

            <ActionLink
              className="text-red-600 hover:bg-red-50"
              variant="ghost"
              onClick={() => {
                setDeleteOpen(true);
                click("click_more_options ", {
                  clickCopy: "Delete Curriculum",
                });
              }}
            >
              {t("actions.delete", { ns: COMMON_NS })}
            </ActionLink>
          </nav>
        </PopoverContent>
      </Popover>

      {duplicateOpen && (
        <DuplicateCurriculumModalContainer
          curriculum={curriculum}
          open={duplicateOpen}
          setOpen={setDuplicateOpen}
          shouldRedirectOnAction={shouldRedirectOnAction}
        />
      )}

      {deleteOpen && (
        <DeleteCurriculumModalContainer
          curriculum={curriculum}
          open={deleteOpen}
          shouldRedirectOnAction={shouldRedirectOnAction}
          onClose={() => setDeleteOpen(false)}
        />
      )}

      {editOpen && (
        <EditCurriculumModalContainer
          curriculumId={curriculum.id || ""}
          open={editOpen}
          setOpen={setEditOpen}
        />
      )}
    </>
  );
};
